import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { post } from './api'
import { t } from './i18n'
import type { PushState } from './push'

const CONSENT = 'worx.nativePush.user'
const SUB = 'worx.nativePush.subscription'
export const NATIVE_PUSH_CHANGED = 'worx:native-push-changed'
let currentUser: string | null = null
let registeredUser: string | null = null
let queue: Promise<unknown> = Promise.resolve()

export const isNativeIos = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'

function serial<T>(work: () => Promise<T>): Promise<T> {
  const result = queue.then(work)
  queue = result.catch(() => undefined)
  return result
}
function changed() { window.dispatchEvent(new Event(NATIVE_PUSH_CHANGED)) }
function subscription(): { id: string; userId: string } | null {
  try { return JSON.parse(localStorage.getItem(SUB) ?? 'null') } catch { return null }
}
function deviceId(): string {
  const key = 'worx.nativePush.device'
  let id = localStorage.getItem(key)
  if (!id) { id = `ios:${crypto.randomUUID()}`; localStorage.setItem(key, id) }
  return id
}

/** Listeners precede register; register() resolves BEFORE Apple's token callback. */
async function token(): Promise<string> {
  const handles: { remove(): Promise<void> }[] = []
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await new Promise<string>((resolve, reject) => {
      timer = setTimeout(() => reject(new Error(t('notify.nativeRetry'))), 20000)
      void (async () => {
        handles.push(await PushNotifications.addListener('registration', value => resolve(value.value)))
        handles.push(await PushNotifications.addListener('registrationError', () => reject(new Error(t('notify.nativeRetry')))))
        await PushNotifications.register()
      })().catch(() => reject(new Error(t('notify.nativeRetry'))))
    })
  } finally {
    clearTimeout(timer)
    await Promise.all(handles.map(handle => handle.remove()))
  }
}

async function detach(): Promise<void> {
  const sub = subscription()
  // Revoke on the server BEFORE clearing the cookie. Failure keeps logout retryable.
  if (sub) await post('/api/push/native/unsubscribe', { subscriptionId: sub.id })
  localStorage.removeItem(SUB)
  registeredUser = null
  await PushNotifications.unregister()
  // Capacitor rejects this before any successful APNs callback; logout still works.
  await PushNotifications.removeAllDeliveredNotifications().catch(() => undefined)
  changed()
}

async function register(userId: string, label?: string): Promise<void> {
  registeredUser = null
  changed()
  const endpoint = await token()
  if (currentUser !== userId) return
  const { subscriptionId } = await post<{ subscriptionId: string }>('/api/push/native/subscribe', {
    endpoint, deviceId: deviceId(), label: label ?? 'Vieques Worx iOS',
  })
  // Save even if logout began during POST: queued detach must revoke this exact row.
  localStorage.setItem(SUB, JSON.stringify({ id: subscriptionId, userId }))
  registeredUser = currentUser === userId ? userId : null
  changed()
}

export async function nativePushState(userId?: string): Promise<PushState> {
  const permission = await PushNotifications.checkPermissions()
  if (permission.receive === 'denied') return 'blocked'
  return permission.receive === 'granted' && registeredUser === userId ? 'on' : 'off'
}

/** Permission is requested only from the explicit Profile button. */
export function enableNativePush(userId: string, label?: string): Promise<PushState> {
  currentUser = userId
  return serial(async () => {
    let permission = await PushNotifications.checkPermissions()
    if (permission.receive !== 'granted' && permission.receive !== 'denied') {
      permission = await PushNotifications.requestPermissions()
    }
    if (permission.receive !== 'granted') {
      if (subscription()) await detach()
      return permission.receive === 'denied' ? 'blocked' : 'off'
    }
    localStorage.setItem(CONSENT, userId)
    await register(userId, label)
    return nativePushState(userId)
  })
}

/** Renew the APNs token on launch/resume, without another permission prompt. */
export function restoreNativePush(userId: string | null): Promise<void> {
  if (!isNativeIos()) return Promise.resolve()
  currentUser = userId
  return serial(async () => {
    if (!userId) {
      registeredUser = null
      await PushNotifications.unregister()
      await PushNotifications.removeAllDeliveredNotifications().catch(() => undefined)
      changed()
      return
    }
    if (currentUser !== userId) return
    if (subscription()?.userId && subscription()?.userId !== userId) await detach()
    if (localStorage.getItem(CONSENT) !== userId) return
    const permission = await PushNotifications.checkPermissions()
    if (permission.receive === 'granted') await register(userId)
    else if (subscription()) await detach()
  })
}

export function stopNativePush(): Promise<void> {
  if (!isNativeIos()) return Promise.resolve()
  currentUser = null
  return serial(detach)
}
